<template>
  <v-dialog
    :id="id"
    v-model="isDialogOpen"
    class="custom-dialog"
    scrollable
    persistent
    max-width="800px"
    :fullscreen="fullscreen"
  >
    <v-card
      :id="bodyId"
      class="custom-dialog-card"
      :class="{ 'custom-dialog-fullscreen': fullscreen }"
    >
      <v-card-item class="custom-dialog-title">
        <template #title>
          <h1>{{ heading }}</h1>
        </template>
        <template #subtitle>
          <span class="custom-dialog-subTitle">{{ subheading }}</span>
        </template>
        <template #append>
          <v-chip
            v-if="showChip"
            :color="valueRequired ? 'pink' : 'default'"
            class="custom-chip mr-3"
          >
            {{ valueRequired ? 'Required' : 'Optional' }}</v-chip
          >
          <v-btn
            v-if="showResizeButton"
            id="custom-dialog-resize-button"
            class="mr-3"
            icon
            variant="plain"
            size="25px"
            @click="toggleFullscreen"
          >
            <v-icon
              v-if="fullscreen"
              v-tooltip:bottom="`Modal view`"
              size="25px"
              color="alertText"
              >{{ 'mdi-fullscreen-exit' }}</v-icon
            >
            <v-icon v-else v-tooltip:bottom="`Full screen view`" size="25px" color="alertText">
              {{ 'mdi-fullscreen' }}
            </v-icon>
          </v-btn>
          <v-btn icon variant="plain" size="24px" @click="onClose">
            <v-icon size="24px" color="alertText" icon="mdi-close" />
          </v-btn>
        </template>
      </v-card-item>
      <div class="custom-dialog-alert">
        <cs-alert dialog />
      </div>
      <div class="custom-dialog-content style-06">
        <slot name="custom-dialog-content" />
      </div>
      <v-card-actions v-if="showButton" class="custom-dialog-actions">
        <cs-button
          v-if="secondaryAction2 && secondaryAction2.label"
          class="custom-dialog-button"
          :class="buttonClass"
          secondary
          :disabled="!!secondaryAction2.disabled || !!secondaryAction2.loading"
          :label="secondaryAction2.label"
          :loading="!!secondaryAction2.loading"
          :left-icon="secondaryAction2.leftIcon"
          :right-icon="secondaryAction2.rightIcon"
          @click="onSecondary2Click()"
        />
        <cs-button
          v-if="secondaryAction1 && secondaryAction1.label"
          :id="secondaryAction1.id"
          class="custom-dialog-button"
          :class="buttonClass"
          secondary
          :disabled="!!secondaryAction1.disabled || !!secondaryAction1.loading"
          :label="secondaryAction1.label"
          :loading="!!secondaryAction1.loading"
          :left-icon="secondaryAction1.leftIcon"
          :right-icon="secondaryAction1.rightIcon"
          @click="onSecondary1Click()"
        />
        <cs-button
          v-if="deleteAction && deleteAction.label"
          class="custom-dialog-button"
          :class="buttonClass"
          delete
          :disabled="!!deleteAction.disabled || !!deleteAction.loading"
          :label="deleteAction.label"
          :loading="!!deleteAction.loading"
          :left-icon="deleteAction.leftIcon"
          :right-icon="deleteAction.rightIcon"
          @click="onDeleteClick()"
        />
        <cs-button
          v-if="primaryAction && primaryAction.label"
          :id="primaryAction.id"
          class="custom-dialog-button"
          :class="buttonClass"
          primary
          :disabled="!!primaryAction.disabled || !!primaryAction.loading"
          :label="primaryAction.label"
          :loading="!!primaryAction.loading"
          :left-icon="primaryAction.leftIcon"
          :right-icon="primaryAction.rightIcon"
          @click="onPrimaryClick()"
        />
      </v-card-actions>
      <div v-else class="custom-dialog-bottom-space" />
    </v-card>
  </v-dialog>
</template>

<script>
import { CSBase } from '@complispace/cs-design-system';

export default {
  name: 'CustomDialog',

  extends: CSBase,

  props: {
    id: { type: String, required: false, default: '' },
    bodyId: { type: String, required: false, default: '' },
    modelValue: { type: Boolean, required: true },
    fullscreen: { type: Boolean, required: false, default: false },
    showResizeButton: { type: Boolean, required: false },
    showChip: { type: Boolean, required: false },
    heading: { type: String, required: true },
    subheading: { type: String, required: true, default: '' },
    primaryAction: { type: Object, required: false, default: undefined },
    secondaryAction1: { type: Object, required: false, default: undefined },
    secondaryAction2: { type: Object, required: false, default: undefined },
    deleteAction: { type: Object, required: false, default: undefined },
    valueRequired: { type: Boolean, required: false }
  },

  emits: [
    'update:modelValue',
    'update:fullscreen',
    'primary-click',
    'secondary1-click',
    'secondary2-click',
    'delete-click'
  ],

  data() {
    return {
      btnWidthFull: false,
      btnHeight: 0
    };
  },

  computed: {
    isDialogOpen: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit('update:modelValue', val);
      }
    },
    buttonClass() {
      if (this.btnWidthFull) {
        return 'custom-dialog-btn-full';
      }
      return '';
    },
    showButton() {
      const primaryExists = (this.primaryAction && this.primaryAction.label) || false;
      const deleteExists = (this.deleteAction && this.deleteAction.label) || false;
      return (primaryExists || deleteExists) && this.getButtonCount() <= 3;
    }
  },

  watch: {
    isDialogOpen(val) {
      if (!val) {
        this.$emit('update:fullscreen', false);
      }
    }
  },

  methods: {
    onClose() {
      this.isDialogOpen = false;
    },
    onPrimaryClick() {
      this.$emit('primary-click');
    },
    onSecondary1Click() {
      this.$emit('secondary1-click');
    },
    onSecondary2Click() {
      this.$emit('secondary2-click');
    },
    onDeleteClick() {
      this.$emit('delete-click');
    },
    async onResize() {
      this.btnWidthFull = false;
      await this.$nextTick();
      this.resetBtnWidth();
    },
    getButtonHeight() {
      const element = document.querySelector('.custom-dialog-button');
      let height = 0;
      if (element) {
        const cssObj = window.getComputedStyle(element, null);
        const margin =
          parseInt(cssObj.getPropertyValue('margin-top'), 10) +
          parseInt(cssObj.getPropertyValue('margin-bottom'), 10);
        height = element.offsetHeight + margin;
      }
      return height;
    },
    resetBtnWidth() {
      if (!this.btnHeight) {
        this.btnHeight = this.getButtonHeight();
      }
      if (!this.btnHeight) {
        return;
      }
      const selector = '.custom-dialog-actions';
      const element = document.querySelector(selector);
      if (element && element.offsetHeight > this.btnHeight * 2) {
        this.btnWidthFull = true;
      } else {
        this.btnWidthFull = false;
      }
    },
    getButtonCount() {
      let count = 0;
      if (this.primaryAction) {
        count += 1;
      }
      if (this.deleteAction) {
        count += 1;
      }
      if (this.secondaryAction1) {
        count += 1;
      }
      if (this.secondaryAction2) {
        count += 1;
      }
      return count;
    },
    toggleFullscreen() {
      this.$emit('update:fullscreen', !this.fullscreen);
    }
  }
};
</script>

<style scoped>
.custom-dialog-card {
  border-radius: 24px !important;
}

.custom-dialog-fullscreen {
  border-radius: 0 !important;
}

.custom-dialog-title {
  display: flex;
  justify-content: space-between;
  align-content: center;
  color: rgb(var(--v-theme-dialogTitle)); /* #212d38; */
  background-color: rgb(var(--v-theme-dialogHeader)); /* #f4f5f6; */
  padding: 16px 24px;
}

.custom-dialog-subTitle {
  opacity: 1 !important;
  color: rgb(var(--v-theme-dialogSubTitle)) !important; /* #000000; */
}

.custom-dialog-content {
  min-height: 72px;
  padding: 16px 24px 0 24px;
  overflow-y: auto;
  height: 100%;
}

.custom-dialog-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  padding: 0 24px 12px !important;
}

.custom-dialog-button {
  margin: 0px 0px 12px 16px !important;
}
.custom-dialog-bottom-space {
  height: 24px;
}

.custom-dialog-btn-full {
  width: 100%;
}

.custom-chip {
  font-weight: 900;
}
</style>
